/**
 * Manutenção › Orçamentos › "Orçamentos da Manutenção" — a fila e o orçamento aberto (mig 782; mockup aprovado em
 * 27/10/2026, `docs/auvo-referencia/mockup-os-aprovado/Main.dc.html`, tela Orçamentos).
 *
 * A fila por situação (A montar · Em andamento · Aprovados · No Compras · Recusados) à esquerda; o orçamento aberto à
 * direita, com os passos do mockup: montar e enviar → a resposta → o Compras. As regras moram no servidor (o técnico não
 * muda preço nem dá desconto, a versão nova ao mexer depois de enviar, a OS de Venda para ir ao Compras): a tela mostra a
 * frase que ele devolve, no lugar do campo.
 */
import { useCallback, useEffect, useState } from 'react';

import { Button, ClienteV2Combobox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, ErroCampo, HelpTip, Input, Label } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

import { DetalheDoOrcamentoPainel } from './DetalheDoOrcamentoPainel';
import { brl, quando } from './formato';
import { ABAS, mensagemDoErro, orcamentoApi, type AbaDaFila, type LinhaDaFila } from './orcamento-api';

const AJUDA =
  'Os orçamentos que a Manutenção monta: material do cadastro do Omie com o markup do catálogo e, quando precisa, mão de obra. ' +
  'O e-mail vai aos e-mails do cliente marcados "Recebe orçamento"; quem tem "Pode aprovar" recebe os botões para responder.';

export function OrcamentosDaManutencao() {
  const [linhas, setLinhas] = useState<LinhaDaFila[]>([]);
  const [contagem, setContagem] = useState<Record<AbaDaFila, number> | null>(null);
  const [aba, setAba] = useState<AbaDaFila>('montar');
  // `?orcamento=<id>`: o número da Home (D71) abre direto no orçamento parado.
  const [aberto, setAberto] = useState<string | null>(() => new URLSearchParams(window.location.search).get('orcamento'));
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const f = await orcamentoApi.fila();
      setLinhas(f.linhas);
      setContagem(f.contagem);
      setErro(null);
    } catch (e) {
      setErro(mensagemDoErro(e, 'Não deu para carregar os orçamentos. Recarregue a página.'));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Aberto pelo link da Home: a fila mostra a aba do orçamento aberto (uma vez), não "A montar" vazia ao lado dele.
  const [abaDoLink, setAbaDoLink] = useState(() => new URLSearchParams(window.location.search).get('orcamento'));
  useEffect(() => {
    if (!abaDoLink) return;
    const l = linhas.find((x) => x.id === abaDoLink);
    if (!l) return;
    const a = ABAS.find((x) => x.status.includes(l.status));
    if (a) setAba(a.id);
    setAbaDoLink(null);
  }, [abaDoLink, linhas]);

  const daAba = linhas.filter((l) => ABAS.find((a) => a.id === aba)!.status.includes(l.status));
  const nomeDaAba = ABAS.find((a) => a.id === aba)!.nome;

  return (
    <div className="flex flex-col gap-4" data-testid="orcamentos-da-manutencao">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1 text-sm text-ink-2 dark:text-ink-dark-2">
          Orçamentos da Manutenção <HelpTip texto={AJUDA} />
        </span>
        <Button onClick={() => setCriando(true)} data-testid="orcamento-novo" className="min-h-[44px] md:min-h-0">
          Novo orçamento
        </Button>
      </div>

      <div role="tablist" aria-label="Situação" className="flex flex-wrap gap-2">
        {ABAS.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={aba === a.id}
            onClick={() => setAba(a.id)}
            data-testid={`orcamento-aba-${a.id}`}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-2 rounded-pill border px-3.5 text-sm font-semibold md:min-h-9',
              aba === a.id
                ? 'border-red bg-red-soft text-red dark:bg-red-soft-dark dark:text-red-glow'
                : 'border-ink-1/20 bg-paper-surface text-ink-2 hover:text-ink-1 dark:border-ink-dark-1/20 dark:bg-paper-dark-surface dark:text-ink-dark-2',
            )}
          >
            {a.nome}
            <span className="text-xs font-bold">{contagem?.[a.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {erro ? (
        <p role="alert" className="text-sm text-red-deep dark:text-red-glow">
          {erro}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(260px,320px)_1fr]">
        <div className="flex flex-col gap-2" data-testid="orcamento-lista">
          {daAba.length === 0 ? (
            <EmptyState title={`Nenhum orçamento em "${nomeDaAba}"`} description={aba === 'montar' ? 'Use "Novo orçamento" para começar um.' : undefined} />
          ) : (
            daAba.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setAberto(l.id)}
                aria-pressed={aberto === l.id}
                data-testid={`orcamento-linha-${l.codigo}`}
                className={cn(
                  'flex min-h-[44px] flex-col gap-0.5 rounded-lg border px-3 py-2 text-left',
                  aberto === l.id ? 'border-red bg-red-soft dark:bg-red-soft-dark' : 'border-line bg-paper-surface dark:border-line-dark dark:bg-paper-dark-surface',
                )}
              >
                <span className="flex items-center justify-between gap-2 text-sm">
                  <span>
                    <strong>{l.codigo}</strong> · {quando(l.criadoEmIso)}
                  </span>
                  <strong>{brl(l.totalCentavos)}</strong>
                </span>
                <span className="text-sm text-ink-1 dark:text-ink-dark-1">{l.clienteNome}</span>
                <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                  {l.titulo || 'Sem título'}
                  {l.origem === 'tecnico' ? ' · feito pelo técnico' : ''}
                  {l.enviadoVezes > 1 ? ` · enviado ${l.enviadoVezes} vezes` : ''}
                </span>
              </button>
            ))
          )}
        </div>

        <div>
          {aberto ? (
            <DetalheDoOrcamentoPainel
              id={aberto}
              aoMudar={(d) => {
                void carregar();
                const novaAba = ABAS.find((a) => a.status.includes(d.status))?.id;
                if (novaAba && novaAba !== aba) setAba(novaAba);
              }}
            />
          ) : (
            <p className="rounded-lg border border-dashed border-line p-6 text-sm text-ink-3 dark:border-line-dark dark:text-ink-dark-3">
              Escolha um orçamento na lista para abrir.
            </p>
          )}
        </div>
      </div>

      <NovoOrcamentoDialog
        aberto={criando}
        aoFechar={() => setCriando(false)}
        aoCriar={(id) => {
          setCriando(false);
          setAba('montar');
          setAberto(id);
          void carregar();
        }}
      />
    </div>
  );
}

function NovoOrcamentoDialog({ aberto, aoFechar, aoCriar }: { aberto: boolean; aoFechar: () => void; aoCriar: (id: string) => void }) {
  const [clienteId, setClienteId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [erros, setErros] = useState<{ cliente?: string; geral?: string }>({});
  const [salvando, setSalvando] = useState(false);
  const { show } = useToast();

  useEffect(() => {
    if (!aberto) return;
    setClienteId('');
    setTitulo('');
    setErros({});
  }, [aberto]);

  async function criar() {
    if (!clienteId) {
      setErros({ cliente: 'Escolha o cliente na lista.' });
      document.getElementById('novo-orcamento-cliente')?.focus();
      return;
    }
    setSalvando(true);
    try {
      const d = await orcamentoApi.criar({ clienteId, titulo: titulo.trim() || undefined });
      show(`Orçamento ${d.codigo} criado.`, { variant: 'success' });
      aoCriar(d.id);
    } catch (e) {
      setErros({ geral: mensagemDoErro(e) });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(o) => !o && aoFechar()} size="md">
      <DialogHeader>
        <DialogTitle>Novo orçamento</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="flex flex-col gap-4">
          <div>
            <Label htmlFor="novo-orcamento-cliente">Cliente</Label>
            <ClienteV2Combobox id="novo-orcamento-cliente" soClientes value={clienteId} onChange={setClienteId} placeholder="Digite o nome do cliente" error={erros.cliente} />
          </div>
          <div>
            <Label htmlFor="novo-orcamento-titulo">Título</Label>
            <Input id="novo-orcamento-titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex.: Fontes para conversor de fibra" maxLength={140} />
          </div>
          <ErroCampo id="novo-orcamento-geral" mensagem={erros.geral} />
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button onClick={criar} loading={salvando} data-testid="novo-orcamento-criar">
          Criar orçamento
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
